import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  DatePicker,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  Tabs,
  Tooltip,
} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import moment from 'moment';
import _ from 'lodash';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {FormCard, spans, Hint} from 'pages/AssetManagement/v2/FormCard';
import {InstrumentActions} from 'pages/AssetManagement/v2/InstrumentActions';
import {
  GET_STOCK_EXCHANGE_DOCUMENT_BY_ID,
  GET_STOCK_EXCHANGE_DOCUMENT_QUERIES,
  SEARCH_SEEDED_COMPANY_DOCUMENTS,
} from 'graphql/queries/stocks';
import {
  CREATE_STOCK_EXCHANGE_DOCUMENT,
  UPDATE_STOCK_EXCHANGE_DOCUMENT,
} from 'graphql/mutations/stocks';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {requestCompanyDocumentTranslation} from 'redux/store/news/actions';
import CompanyPDFUpload from 'pages/documentCenter/create/CompanyPDFUpload';
import {defaultLanguages} from 'pages/news/newsTypes';
import {formatRoleString} from 'utils/helper';
import TranslatedStockExchangeDocument from './TranslatedStockExchangeDocument';
import styles from './createStockExchnageDocument.module.scss';

const DOCUMENTS_ROUTE = '/dashboard/startup/document-category?route=stocks';
const SEEDED_PREFIX = 'daba-finance-company-report-';

const stripPrefix = name =>
  name?.startsWith(SEEDED_PREFIX) ? name.slice(SEEDED_PREFIX.length) : name;

const languageLabel = code =>
  defaultLanguages.find(entry => entry.key === code)?.label ?? code;

/**
 * Create / edit a stock exchange document.
 *
 * Only `stockExchange` and `documentUrlSource` are required by the mutation;
 * everything the form insists on beyond that is a deliberate product rule —
 * a document with no file or no name is not worth storing.
 *
 * The rebuild fixed a set of quiet failures:
 *   • an empty Issued Date was sent as *today*, because `moment(undefined)`
 *     is now rather than invalid;
 *   • Link Type carried a Select `defaultValue` that antd ignores inside a
 *     Form.Item, so the field looked filled and submitted empty;
 *   • the Restrict Language switch had no `valuePropName`, so a saved `true`
 *     never showed when editing;
 *   • picking a second translation language silently did nothing;
 *   • choosing Seeded and selecting no document saved a null url.
 */
const CreateStockExchangeDocument = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {id} = useParams();
  const isEdit = Boolean(id);

  const [limit, setLimit] = useState(50);
  const [searchResult, setSearchResult] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [viewPDF, setViewPDF] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [translatedData, setTranslatedData] = useState([]);
  const [activeTab, setActiveTab] = useState('original');

  // Read straight from the form rather than mirroring into state — the old
  // screen kept `documentName` in both and submitted the copy.
  const urlType = Form.useWatch('type', form) ?? 'UPLOADED';
  const language = Form.useWatch('language', form);
  const documentName = Form.useWatch('documentName', form);
  const targetLanguage = Form.useWatch('translationLanguage', form);

  const {data, loading} = useQuery(GET_STOCK_EXCHANGE_DOCUMENT_QUERIES, {
    errorPolicy: 'all',
    onError: () =>
      toast.error('Could not fetch Stock Exchange Document Categories'),
  });

  const {data: existing, loading: loadingExisting} = useQuery(
    GET_STOCK_EXCHANGE_DOCUMENT_BY_ID,
    {
      variables: {documentId: id},
      // Ran on create too, asking the server for `documentId: undefined`.
      skip: !isEdit,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );

  const [translateText, {loading: translating}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );
  const [addDocument, {loading: creating}] = useMutation(
    CREATE_STOCK_EXCHANGE_DOCUMENT,
    {errorPolicy: 'all'},
  );
  const [updateDocument, {loading: updating}] = useMutation(
    UPDATE_STOCK_EXCHANGE_DOCUMENT,
    {errorPolicy: 'all'},
  );

  const translationTargets = useMemo(
    () => defaultLanguages.filter(entry => entry.key !== language),
    [language],
  );

  useEffect(() => {
    const record = existing?.getOneStockExchangeDocument?.data;
    if (!record) return;
    setSelectedDocument(record.documentUrl);
    setViewPDF(record.documentUrl);
    setPdfFile(record.documentUrl);
    setPdfName(record.documentName);
    form.setFieldsValue({
      stockId: record.stockExchange?.id,
      documentCategory: record.documentCategory,
      language: record.language,
      documentName: record.documentName,
      document: record.documentName,
      type: record.documentUrlSource,
      restrictLanguage: record.restrictLanguage ?? false,
      issuedDate: record.issuedDate ? moment(record.issuedDate) : null,
    });
    setTranslatedData(
      (record.multilang ?? []).map(entry => ({
        documentName: entry?.documentName,
        documentUrl:
          entry?.documentUrlSource === 'SEEDED' ? entry?.documentUrl : null,
        documentUrlSource: entry?.documentUrlSource,
        pdfFile:
          entry?.documentUrlSource === 'UPLOADED' ? entry?.documentUrl : null,
        pdfName: entry?.documentName,
        language: entry?.language,
      })),
    );
  }, [existing?.getOneStockExchangeDocument]);

  const {
    loading: loadingSeeded,
    refetch,
    networkStatus,
    fetchMore,
  } = useQuery(SEARCH_SEEDED_COMPANY_DOCUMENTS, {
    variables: {
      filters: {
        isAddedToDocumentCenter: false,
        language,
        pagination: {limit},
      },
    },
    // The seeded list is filtered by language; asking before one is chosen
    // returns the wrong set.
    skip: !language || urlType !== 'SEEDED',
    onCompleted: result =>
      setSearchResult(result?.getSeededStockExchangeDocuments?.documents ?? []),
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const debouncedSearch = useMemo(
    () =>
      _.debounce(value => {
        refetch({
          filters: {
            ...(value ? {companyTickers: [value.toUpperCase()]} : {}),
            isAddedToDocumentCenter: false,
            language,
            pagination: {limit, page: 1},
          },
        });
      }, 800),
    [language, limit, refetch],
  );

  const onScroll = event => {
    const target = event.target;
    if (
      !loadingSeeded &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      const next = limit + 50;
      setLimit(next);
      fetchMore({
        variables: {
          filters: {
            isAddedToDocumentCenter: false,
            language,
            pagination: {limit: next},
          },
        },
      });
    }
  };

  const handleTranslation = async () => {
    // Was `!language && !documentName`, so it only complained when both were
    // missing and produced nonsense when one was.
    if (!language || !documentName) {
      return toast.error(
        'Choose a base language and enter a document name before translating',
      );
    }
    if (!targetLanguage) {
      return toast.error('Choose a language to translate into');
    }

    const translation = await requestCompanyDocumentTranslation(translateText, {
      sourceLang: language,
      targetLang: targetLanguage,
      targetText1: documentName,
    });
    if (!translation) return toast.error('Could not translate that name');

    setTranslatedData(current => {
      const next = [...current];
      const index = next.findIndex(item => item?.language === targetLanguage);
      if (index > -1) {
        // Keep whatever file the admin already attached to this language.
        next[index] = {...next[index], documentName: translation.documentName};
        return next;
      }
      // A translation for a *different* language used to make this a no-op,
      // so a second target language silently did nothing.
      next.push({
        ...translation,
        language: targetLanguage,
        documentUrlSource: translation.documentUrlSource || 'UPLOADED',
      });
      return next;
    });
    setActiveTab(targetLanguage);
  };

  const outstanding = useMemo(() => {
    const problems = [];
    if (urlType === 'UPLOADED' && !pdfFile) problems.push('a PDF upload');
    // Selecting Seeded and picking nothing used to save a null url.
    if (urlType === 'SEEDED' && !selectedDocument)
      problems.push('a seeded document');
    translatedData.forEach(entry => {
      const file =
        entry?.documentUrlSource === 'SEEDED'
          ? entry?.documentUrl
          : entry?.pdfFile;
      if (!file) problems.push(`a file for ${languageLabel(entry?.language)}`);
      if (!entry?.documentName)
        problems.push(`a name for ${languageLabel(entry?.language)}`);
    });
    return problems;
  }, [urlType, pdfFile, selectedDocument, translatedData]);

  const handleSubmit = values => {
    if (outstanding.length)
      return toast.error(`Still needed: ${outstanding[0]}`);

    const resolver = isEdit ? updateDocument : addDocument;
    resolver({
      variables: {
        data: {
          ...(isEdit && {documentId: id}),
          stockExchange: values.stockId,
          documentCategory: values.documentCategory,
          documentName: values.documentName,
          documentUrl: urlType === 'UPLOADED' ? pdfFile : selectedDocument,
          documentUrlSource: urlType,
          language: values.language,
          restrictLanguage: values.restrictLanguage ?? false,
          // `moment(undefined).format()` is *now*, so an untouched optional
          // date silently stamped today.
          ...(values.issuedDate && {
            issuedDate: moment(values.issuedDate).format(),
          }),
          ...(translatedData.length && {
            multilang: translatedData.map(entry => ({
              language: entry?.language,
              documentName: entry?.documentName,
              documentUrlSource: entry?.documentUrlSource,
              documentUrl:
                entry?.documentUrlSource === 'SEEDED'
                  ? entry?.documentUrl
                  : entry?.pdfFile,
            })),
          }),
        },
      },
    })
      .then(({data: result}) => {
        const payload =
          result?.[
            isEdit ? 'updateStockExchangeDocument' : 'addStockExchangeDocument'
          ];
        if (payload?.__typename === 'Error')
          return toast.error(payload.message);
        toast.success(`Document ${isEdit ? 'updated' : 'created'}`);
        navigate(DOCUMENTS_ROUTE);
      })
      // Was `toast.error(error)` — an Error object, which renders as nothing.
      .catch(error => toast.error(error.message));
  };

  if (isEdit && loadingExisting) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading document…</h1>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'original',
      label: language ? languageLabel(language) : 'Original',
      children: (
        <>
          <div className={spans.full}>
            <Form.Item
              name="documentName"
              label="Document name"
              rules={[
                {required: true, message: 'A document name is required'},
              ]}>
              <Input placeholder="BRVM monthly bulletin — August 2026" />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="type"
              label="Link type"
              rules={[
                {required: true, message: 'Choose where the file comes from'},
              ]}>
              <Select>
                <Select.Option value="UPLOADED">Upload PDF</Select.Option>
                <Select.Option value="SEEDED">Seeded document</Select.Option>
              </Select>
            </Form.Item>
          </div>

          {urlType === 'SEEDED' ? (
            <div className={spans.full}>
              <Form.Item
                name="document"
                label={
                  <span>
                    Search by ticker and select{' '}
                    <Tooltip title="Search matches the company ticker, not the document name. Results are filtered to the base language.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                rules={[{required: true, message: 'Select a seeded document'}]}>
                <Select
                  showSearch
                  filterOption={false}
                  onSearch={debouncedSearch}
                  onPopupScroll={onScroll}
                  loading={loadingSeeded}
                  placeholder={
                    language
                      ? 'Search by company ticker'
                      : 'Choose a base language first'
                  }
                  disabled={!language}
                  onChange={value => {
                    if (!value) return setSelectedDocument(null);
                    setSelectedDocument(JSON.parse(value)?.documentLink);
                  }}>
                  {/* `searchResult.length &&` rendered a bare 0 as a child
                      when the list was empty. */}
                  {searchResult.map(entry => (
                    <Select.Option
                      key={entry?.documentName}
                      value={JSON.stringify(entry)}>
                      {stripPrefix(entry?.documentName)}
                    </Select.Option>
                  ))}
                  {networkStatus === 2 && (
                    <Select.Option disabled key="__loading">
                      Loading…
                    </Select.Option>
                  )}
                </Select>
              </Form.Item>
              {selectedDocument && (
                <CompanyPDFUpload
                  onlyButtons
                  type="SINGLE"
                  pdfFile={selectedDocument}
                  viewPdf={selectedDocument}
                />
              )}
            </div>
          ) : (
            <div className={spans.full}>
              <CompanyPDFUpload
                title="Upload the document (PDF)"
                existingData={viewPDF}
                pdfName={pdfName || 'Supported format: pdf'}
                setPdfName={setPdfName}
                setSinglePDFFile={setPdfFile}
                setViewPDFLink={setViewPDF}
                type="SINGLE"
                viewPdf={viewPDF}
                pdfFile={pdfFile}
                handleDeleteFile={() => {
                  setPdfFile(null);
                  setPdfName('Supported format: pdf');
                  setViewPDF(null);
                }}
                index="original"
              />
            </div>
          )}
        </>
      ),
    },
    ...translatedData.map((item, index) => ({
      key: item?.language ?? String(index),
      label: languageLabel(item?.language),
      children: (
        <div className={spans.full}>
          <TranslatedStockExchangeDocument
            index={index}
            data={item}
            setTranslatedData={setTranslatedData}
            translatedData={translatedData}
          />
        </div>
      ),
    })),
  ];

  return (
    <div className={styles.root}>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <button
              type="button"
              className={shell.back}
              onClick={() => navigate(DOCUMENTS_ROUTE)}>
              ← Documents
            </button>
            <h1 className={shell.title}>
              {isEdit ? 'Edit document' : 'New exchange document'}
            </h1>
            <div className={shell.sub}>
              Published against one exchange, in one base language, optionally
              translated.
            </div>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        // A real initial value, not the Select `defaultValue` the old screen
        // used — antd ignores that inside a Form.Item, so the field looked
        // pre-filled and submitted empty.
        initialValues={{type: 'UPLOADED', restrictLanguage: false}}
        onFinish={handleSubmit}>
        <FormCard
          title="Where it belongs"
          description="The exchange and the shelf this document sits on">
          <div className={spans.half}>
            <Form.Item
              name="stockId"
              label="Stock exchange"
              rules={[{required: true, message: 'Choose an exchange'}]}>
              <Select loading={loading} showSearch optionFilterProp="children">
                {data?.getAllStocksExchange?.map(entry => (
                  <Select.Option key={entry?.id} value={entry?.id}>
                    {entry?.stockExchangeName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="documentCategory"
              label="Category"
              rules={[{required: true, message: 'Choose a category'}]}>
              <Select loading={loading} showSearch optionFilterProp="children">
                {data?.getStockExchangeDocumentCategory?.data?.map(entry => (
                  <Select.Option key={entry?.label} value={entry?.label}>
                    {formatRoleString(entry?.name)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="issuedDate" label="Issued date">
              <DatePicker
                className={styles.fullWidth}
                format="DD MMM YYYY, HH:mm"
                showTime={{defaultValue: moment('00:00', 'HH:mm:ss')}}
              />
            </Form.Item>
            <Hint>Optional. Left blank, no date is stored.</Hint>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="restrictLanguage"
              label="Restrict to its language"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Hint>
              On, the document is only shown to readers in its own language.
            </Hint>
          </div>
        </FormCard>

        <FormCard
          title="Languages"
          description="The base language decides which seeded documents are searchable">
          <div className={spans.half}>
            <Form.Item
              name="language"
              label="Base language"
              rules={[{required: true, message: 'Choose a base language'}]}>
              <Select
                onChange={() => {
                  // The seeded list is language-filtered, so a stale pick
                  // would point at a document from the other language.
                  setSelectedDocument(null);
                  form.setFieldsValue({document: undefined});
                }}>
                {defaultLanguages.map(entry => (
                  <Select.Option key={entry.key} value={entry.key}>
                    {entry.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="translationLanguage" label="Translate into">
              <Select
                allowClear
                disabled={!language}
                placeholder={
                  language ? 'Choose a language' : 'Pick a base language first'
                }>
                {translationTargets.map(entry => (
                  <Select.Option key={entry.key} value={entry.key}>
                    {entry.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="The document"
          description="A name and a file per language. Each translation needs its own file.">
          <div className={spans.full}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              tabBarExtraContent={
                targetLanguage && (
                  <Button
                    label={translating ? 'Translating…' : 'Translate name'}
                    disabled={translating || !documentName}
                    onClick={handleTranslation}
                  />
                )
              }
            />
          </div>
        </FormCard>

        <InstrumentActions
          remaining={outstanding.length}
          disabled={creating || updating}
          label={
            creating || updating
              ? 'Saving…'
              : isEdit
              ? 'Save changes'
              : 'Create document'
          }
        />
      </Form>
    </div>
  );
};

export default CreateStockExchangeDocument;
